import React, { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
// import { useClickOutside } from 'react-haiku';
import { twMerge } from 'tailwind-merge';
import Datetime from 'react-datetime';
import classnames from 'classnames';
import moment from 'moment';
import 'moment/locale/lv';

import Show from 'components/Show';
import { Button } from 'components/Misc/Form';
import { Title } from '../Typography';

const Datepicker = ({ end, time, asInput, readOnly, placeholder, label, name, value, onChange, className }) => {
	// Store modal's state
	const [modal, setModal] = useState(false);

	// Toggle modal's state
	const handleModal = () => {
		if (!readOnly) setModal(m => !m);
	};

	// Container's classnames
	const containerClassName = classnames(
		'flex flex-col',
		className
	);

	// Component's classname
	const pillClassName = classnames(
		'h-7 px-3 max-w-fit rounded-full tracking-wide text-sm leading-7 whitespace-nowrap text-ellipsis overflow-hidden transition-colors bg-neutral-50 text-neutral-800 dark:bg-neutral-700 dark:text-white',
		!readOnly && 'hover:bg-neutral-100 dark:hover:bg-neutral-600 cursor-pointer'
	);

	// Handle input's value change
	const handle = date => {
		onChange({
			name,
			value: end ? date.endOf('day').toISOString()
				: time ? date.startOf('minute').toISOString()
					: date.startOf('day').toISOString()
		});
	};

	// Handle input's value reset
	const reset = () => {
		onChange({ name, value: '' });
	};

	// Size calculation
	const size = useMemo(() => {
		if (value && time) {
			return moment(value).format('D. MMM, YYYY HH:mm');
		} else if (value) {
			return moment(value).format('D. MMM, YYYY');
		} else {
			return 'izvēlēties';
		}
	}, [value, time]);

	return (
		<div className={containerClassName}>
			{/* Show label only if passed */}
			<Show if={label}>
				<label
					className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors"
				>
					{label}
				</label>
			</Show>

			{/* Show datepicker as input */}
			<Show if={asInput}>
				<input
					readOnly
					disabled={readOnly}
					value={size}
					placeholder="asd"
					className="px-3 h-9 flex items-center w-full bg-transparent border border-neutral-200 dark:border-neutral-700 dark:text-white text-neutral-800 placeholder:text-neutral-400 rounded-lg focus:border-transparent focus:ring focus:ring-amber-500 disabled:cursor-not-allowed disabled:focus:ring-0 cursor-pointer transition-all"
					onFocus={handleModal}
				/>
			</Show>

			{/* Show datepicker as component */}
			<Show if={!asInput}>
				<div
					className={pillClassName}
					onClick={handleModal}
				>
					{size}
				</div>
			</Show>

			{/* Datepicker's modal */}
			<Modal
				time={time}
				value={value}
				onChange={handle}
				onReset={reset}
				modal={modal}
				handleModal={handleModal}
			/>
		</div>
	);
};

const Modal = ({ time, value, modal, handleModal, onChange, onReset }) => {
	// Store container's ref
	const ref = useRef(null);

	// Use outside handler
	// TODO: Fix issue with modal
	// useClickOutside(ref, handleModal);

	// Custom day renderer
	const renderDay = (props, current) => {
		const cls = twMerge(
			'relative pointer leading-[100%] text-lg text-neutral-700 dark:text-white cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg transition-all',
			(current.day() === 0 || current.day() === 6) && 'text-rose-500 dark:text-rose-500',
			props.className.includes('rdtOld') && 'text-neutral-300 dark:text-neutral-600',
			props.className.includes('rdtNew') && 'text-neutral-300 dark:text-neutral-600',
			props.className.includes('rdtToday') && 'font-semibold text-emerald-500 dark:text-emerald-500',
			props.className.includes('rdtActive') && 'text-white dark:text-white bg-emerald-500 hover:bg-emerald-500 dark:hover:bg-emerald-500',
			props.className,
		);

		return (
			<td {...props} className={cls}>
				<div className="w-full pt-[100%] block select-none pointer-events-none"></div>
				<div className="absolute block top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">{current.date()}</div>
			</td>
		);
	};

	// Custom month renderer
	const renderMonth = (props, month) => {
		const months = moment.localeData().monthsShort();
		const cls = twMerge(
			'relative pointer capitalize text-lg text-neutral-700 dark:text-white cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg transition-all',
			props.className.includes('rdtActive') && 'text-white bg-emerald-500 hover:bg-emerald-500 dark:hover:bg-emerald-500',
			props.className,
		);
	
		return (
			<td {...props} className={cls}>
				<div className="w-full pt-[100%] block select-none pointer-events-none"></div>
				<div className="absolute block top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">{months[month]}</div>
			</td>
		);
	};

	// Custom year renderer
	const renderYear = (props, year) => {
		const cls = twMerge(
			'relative pointer ext-lg text-neutral-700 dark:text-white cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg transition-all',
			props.className.includes('rdtActive') && 'text-white bg-emerald-500 hover:bg-emerald-500 dark:hover:bg-emerald-500',
			props.className,
		);

		return (
			<td {...props} className={cls}>
				<div className="w-full pt-[100%] block select-none pointer-events-none"></div>
				<div className="absolute block top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">{year}</div>
			</td>
		);
	};

	// Custom time renderer
	const renderView = (mode, renderDefault) => {
		// Do not handle other views than time
		if (mode !== 'time') return renderDefault();

		// Custom time selection
		const custom = [
			{ key: 1, hours: 9, minutes: 0, text: '9:00' },
			{ key: 2, hours: 10, minutes: 0, text: '10:00' },
			{ key: 3, hours: 11, minutes: 0, text: '11:00' },
			{ key: 4, hours: 18, minutes: 0, text: '18:00'},
			{ key: 5, hours: 19, minutes: 0, text: '19:00' },
			{ key: 6, hours: 20, minutes: 0, text: '20:00' }
		];

		// Custom time selection handler
		const onCustomTime = ({ hours, minutes }) => {
			const current = value ? moment(value) : moment();
			onChange(current.hour(hours).minute(minutes));
		};

		return (
			<div>
				{renderDefault()}

				<div className="mt-4 space-y-2">
					<Title
						title="Pielāgoti laiki"
					/>

					<div className="flex flex-wrap gap-2">
						{custom.map(time =>
							<div
								key={time.key}
								onClick={() => onCustomTime(time)}
								className="px-3 max-w-fit rounded-full tracking-wide text-sm leading-7 whitespace-nowrap text-ellipsis overflow-hidden transition-colors bg-neutral-50 text-neutral-800 dark:bg-neutral-700 dark:text-white hover:bg-neutral-100 dark:hover:bg-neutral-600 cursor-pointer"
							>
								{time.text}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Do not create portal if modal is not visible
	if (!modal) return null;

	return createPortal(
		<div className="fixed top-0 right-0 bottom-0 left-0 z-30 flex justify-center items-center bg-[rgba(0,0,0,0.15)] backdrop-blur-sm">
			<div ref={ref} className="p-4 max-w-md space-y-4 bg-white dark:bg-neutral-800 rounded-lg">
				<Datetime
					input={false}
					locale="lv"
					dateFormat="D. MMM, YYYY"
					timeFormat={time ? 'HH:mm' : false}
					value={moment(value)}
					onChange={onChange}
					renderDay={renderDay}
					renderMonth={renderMonth}
					renderYear={renderYear}
					renderView={renderView}
					timeConstraints={{
						minutes: {
							step: 5
						}
					}}
				/>

				<div className="flex justify-end space-x-2">
					<Button
						icon="close"
						value="Atiestatīt"
						onClick={onReset}
					/>

					<Button
						icon="done"
						theme="success"
						value="Saglabāt"
						onClick={handleModal}
					/>
				</div>
			</div>
		</div>
		, document.body);
};

export default Datepicker;